import { useMutation, useQuery } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useRef, useState } from "react";
import { useLocation } from "wouter";
import { PageHeader } from "@/components/page-header";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";

export default function SalaryTables() {
  const [, navigate] = useLocation();
  const { hasPermission } = useAuth();
  const canManageSalaryTables = hasPermission("salary_tables");
  const { toast } = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const editInputRef = useRef<HTMLInputElement>(null);

  const { data: tables = [], isLoading } = useQuery(orpc.salaryTables.listTables.queryOptions());

  const createMutation = useMutation({
    mutationFn: (name: string) => rpcClient.salaryTables.createTable({ name }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.salaryTables.listTables.queryOptions().queryKey,
      });
      setCreateOpen(false);
      setNewName("");
      toast({ title: "Tabela criada com sucesso" });
    },
    onError: () => toast({ title: "Erro ao criar tabela", variant: "destructive" }),
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      rpcClient.salaryTables.updateTable({ id, name }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.salaryTables.listTables.queryOptions().queryKey,
      });
      setEditingId(null);
    },
    onError: () => {
      toast({ title: "Erro ao renomear tabela", variant: "destructive" });
      setEditingId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => rpcClient.salaryTables.deleteTable({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.salaryTables.listTables.queryOptions().queryKey,
      });
      setDeleteTarget(null);
      toast({ title: "Tabela removida" });
    },
    onError: () => toast({ title: "Erro ao remover tabela", variant: "destructive" }),
  });

  const handleCardClick = (id: string) => {
    navigate(`/salary-tables/${id}`);
  };

  const startRename = (e: React.MouseEvent, table: { id: string; name: string }) => {
    e.stopPropagation();
    setEditingId(table.id);
    setEditingName(table.name);
    setTimeout(() => editInputRef.current?.select(), 0);
  };

  const handleNameBlur = (id: string) => {
    const original = tables.find((t) => t.id === id)?.name;
    if (editingName.trim() && editingName.trim() !== original) {
      renameMutation.mutate({ id, name: editingName.trim() });
    } else {
      setEditingId(null);
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent, id: string) => {
    if (e.key === "Enter") handleNameBlur(id);
    if (e.key === "Escape") setEditingId(null);
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-muted-foreground">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-6">
      <PageHeader title="Tabelas Salariais">
        {canManageSalaryTables && (
          <Button data-testid="button-new-table" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nova Tabela
          </Button>
        )}
      </PageHeader>

      {tables.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          Nenhuma tabela salarial cadastrada.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
          {tables.map((table) => (
            <Card
              key={table.id}
              data-testid={`card-salary-table-${table.id}`}
              className="cursor-pointer hover:shadow-md transition-shadow relative group"
              onClick={() => editingId !== table.id && handleCardClick(table.id)}
            >
              <CardContent className="p-5 min-h-28 flex flex-col justify-between">
                {editingId === table.id ? (
                  <Input
                    ref={editInputRef}
                    data-testid="input-rename-table"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onBlur={() => handleNameBlur(table.id)}
                    onKeyDown={(e) => handleNameKeyDown(e, table.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="h-7 text-sm font-medium"
                    autoFocus
                  />
                ) : (
                  <span className="font-medium text-sm">{table.name}</span>
                )}
                {canManageSalaryTables && editingId !== table.id && (
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`button-rename-table-${table.id}`}
                      className="h-7 w-7 opacity-0 group-hover:opacity-100"
                      onClick={(e) => startRename(e, table)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`button-delete-table-${table.id}`}
                      className="h-7 w-7 opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget(table);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Tabela Salarial</DialogTitle>
          </DialogHeader>
          <Input
            data-testid="input-new-table-name"
            placeholder="Nome da tabela"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) =>
              e.key === "Enter" && newName.trim() && createMutation.mutate(newName.trim())
            }
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancelar
            </Button>
            <Button
              data-testid="button-create-table"
              onClick={() => newName.trim() && createMutation.mutate(newName.trim())}
              disabled={!newName.trim() || createMutation.isPending}
            >
              Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover tabela</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover "{deleteTarget?.name}"? Todas as faixas serão removidas
              também. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
